import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { DATA_PROFILES, hasCurrentProfile } from "app/utils/appHelpers";
import AssistantWidget from "./AssistantWidget";

// Xpit Assistant está en todas las páginas para quien tiene acceso a datos. Una página puede sumarle lo que el usuario
// está viendo (context, va con cada mensaje), atajos propios y subir el botón si tapa algo suyo (useAssistantPage).
const DEFAULT_PAGE_CONFIG = {
    context: {},
    quickActions: [],
    bottomOffset: 96,
};

const AssistantLayoutContext = createContext({
    ...DEFAULT_PAGE_CONFIG,
    isOpen: false,
    setIsOpen: () => {},
    setPageConfig: () => {},
    resetPageConfig: () => {},
});

export const AssistantLayoutProvider = ({ children }) => {
    const [pageConfig, setPageConfigState] = useState(DEFAULT_PAGE_CONFIG);
    const [isOpen, setIsOpen] = useState(false);

    const setPageConfig = useCallback((config = {}) => {
        setPageConfigState({ ...DEFAULT_PAGE_CONFIG, ...config });
    }, []);

    const resetPageConfig = useCallback(() => setPageConfigState(DEFAULT_PAGE_CONFIG), []);

    const value = useMemo(() => ({
        ...pageConfig,
        isOpen,
        setIsOpen,
        setPageConfig,
        resetPageConfig,
    }), [pageConfig, isOpen, resetPageConfig, setPageConfig]);

    return (
        <AssistantLayoutContext.Provider value={value}>
            {children}
        </AssistantLayoutContext.Provider>
    );
};

export const useAssistantLayoutContext = () => useContext(AssistantLayoutContext);

// La página aporta su contexto mientras está montada; al salir, el asistente sigue abierto sin él.
export const useAssistantPage = (config) => {
    const { setPageConfig, resetPageConfig } = useAssistantLayoutContext();

    useEffect(() => {
        setPageConfig(config);
    }, [config, setPageConfig]);

    useEffect(() => () => {
        resetPageConfig();
    }, [resetPageConfig]);
};

export const AssistantLayoutPanel = () => {
    const { authUser } = useJumboAuth();
    const { context, quickActions, bottomOffset, isOpen, setIsOpen } = useAssistantLayoutContext();
    const enabled = hasCurrentProfile(authUser, DATA_PROFILES);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const notifyResize = () => {
            window.dispatchEvent(new Event("resize"));
        };

        notifyResize();
        const timeoutId = window.setTimeout(notifyResize, 320);
        return () => window.clearTimeout(timeoutId);
    }, [isOpen]);

    if (!enabled) return null;

    return (
        <AssistantWidget
            key={authUser?.current_company?.id || "assistant"}
            context={context}
            pageQuickActions={quickActions}
            bottomOffset={bottomOffset}
            inline
            open={isOpen}
            onOpenChange={setIsOpen}
        />
    );
};

export default AssistantLayoutContext;
